import React, { useState } from 'react';
import { Layers, PresentationIcon, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import styles from './PresentationCard.module.css';

const DeleteConfirmDialog = ({ isOpen, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    const handleModalClick = (e) => {
        e.stopPropagation();  // Prevent card click
    };

    return (
        <div
            className={styles.modalOverlay}
            onClick={handleModalClick}
        >
            <div
                className={styles.modalContent}
                onClick={handleModalClick}
            >
                <h3>Delete Presentation</h3>
                <p>Do you want to permanently delete this presentation?</p>
                <div className={styles.modalButtons}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onCancel(e);
                        }}
                        className={styles.cancelButton}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onConfirm(e);
                        }}
                        className={styles.deleteConfirmButton}
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

const PresentationCard = ({ content, onClick, onDelete }) => {
    // eslint-disable-next-line
    const { user } = useAuth();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const slideCount = content.content.split(/(?=## )/).filter(section => section.trim()).length;

    const highlightText = (text, searchQuery) => {
        if (!searchQuery) return text;

        const regex = new RegExp(`(${searchQuery})`, 'gi');
        return text.split(regex).map((part, i) =>
            regex.test(part) ? (
                <span key={i} className={styles.highlight}>{part}</span>
            ) : part
        );
    };

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        await onDelete(content._id);
        setShowDeleteConfirm(false);
    };

    const handleDeleteCancel = (e) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
    };

    return (
        <div className={styles.card} onClick={onClick}>
            <div className={styles.imageContainer}>
                {!imageLoaded && !imageError && (
                    <div className={styles.placeholder}>
                        <PresentationIcon size={24} className={styles.placeholderIcon} />
                    </div>
                )}
                <img
                    src={content.thumbnail}
                    alt={content.title}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                    className={`${styles.image} ${imageLoaded ? styles.loaded : ''}`}
                />
                <div className={styles.slideCount}>
                    <Layers size={16} />
                    <span>{slideCount} slides</span>
                </div>
            </div>

            <div className={styles.content}>
                <h3 className={styles.title}>
                    {content.matches?.title ?
                        highlightText(content.title, content.searchQuery) :
                        content.title}
                </h3>
                <div className={styles.info}>
                    {/* Removed the "Created by" section */}
                    <div className={styles.bottomRow}>
                        <div className={styles.date}>
                            {new Date(content.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </div>
                        <div className={styles.bottomRowButtons}>
                            {/* Removed privacy toggle, kept only delete button */}
                            <button
                                className={styles.deleteButton}
                                onClick={handleDeleteClick}
                                title="Delete presentation"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                </div>
                <DeleteConfirmDialog
                    isOpen={showDeleteConfirm}
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />
            </div>
        </div>
    );
};

export default PresentationCard;